import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Building2, Cpu, Headset, GraduationCap, Briefcase, PhoneCall, Video, Code2, Sparkles, Check, X, Layers, ShieldCheck, Mail, Search, FileLock2, Users2, ArrowRight, CalendarCheck2, Play, Quote } from "lucide-react";
import { SectionHead, Reveal } from "@/components/public/home/ui";
import { useDemo } from "@/components/public/DemoDialog";

const R = { voice: ["AI telephonic", PhoneCall], video: ["AI video", Video], coding: ["Coding round", Code2] };
const SEGMENTS = {
  it: { label: "IT Consultancies & Staffing", Icon: Building2, rounds: ["voice", "video", "coding"], roles: ["Java Developer", "SAP Consultant", "Cloud Architect", "Contract QA"],
    title: "Submit vetted profiles to clients before competitors finish their first call.",
    points: ["Clients, accounts and service lines to organise every requisition", "Branded candidate reports — choose which job details clients see", "Bulk CSV invites for bench and high-volume drives", "Client dashboard to track open roles and progress"] },
  tech: { label: "Tech & Product Companies", Icon: Cpu, rounds: ["voice", "video", "coding"], roles: ["Backend Engineer", "DevOps Engineer", "Data Analyst", "Product Manager"],
    title: "Give hiring managers evidence, not just résumés.",
    points: ["Proctored coding round with integrity signals", "Consistent questions and scoring for every applicant", "Multi-stage pipelines: telephonic → video → coding", "Role-based access for TA, hiring leads and viewers"] },
  bpo: { label: "BPO & Contact Centres", Icon: Headset, rounds: ["voice", "video"], roles: ["Voice Process Associate", "Chat Support", "Collections Agent", "Team Lead"],
    title: "Hear how every applicant sounds on a real call — before you meet them.",
    points: ["Telephonic round tests spoken communication on an actual phone call", "Knockout questions on shifts, location and availability", "Hundreds of applicants screened a day, around the clock", "Same questions and scoring for every applicant, at every site"] },
  campus: { label: "Campus & Volume Hiring", Icon: GraduationCap, rounds: ["voice", "video", "coding"], roles: ["Graduate Trainee", "Fresher Developer", "Management Trainee", "Sales Associate"],
    title: "Turn a 1,000-applicant drive into a ranked shortlist overnight.",
    points: ["Invite an entire campus with one CSV upload", "Candidates interview 24/7 from any browser or phone", "Automatic reminders and Incomplete tracking for drop-offs", "Rank everyone on the same rubric — no interviewer fatigue"] },
  rpo: { label: "RPO & Enterprise HR", Icon: Briefcase, rounds: ["voice", "video"], roles: ["Relationship Manager", "Store Manager", "Sales Executive", "Patient Coordinator"],
    title: "One platform for every client, business unit and location.",
    points: ["Separate workspaces and clients with isolated data", "Customisable role permissions and team scoping", "Branded emails and reports for each organisation", "Analytics with PDF, Excel and CSV exports for leadership"] },
};

function SegmentCard({ s }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-xl shadow-slate-900/5 p-6 sm:p-7" data-testid="segment-card">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-slate-900 grid place-items-center"><s.Icon className="w-5 h-5 text-white" /></div>
        <div><div className="text-xs text-slate-500">Recommended setup</div><div className="font-display text-lg font-semibold text-slate-900">{s.label}</div></div>
      </div>
      <div className="mt-6 text-[11px] font-mono uppercase tracking-widest text-slate-400">Interview rounds</div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {Object.entries(R).map(([k, [l, I]]) => {
          const on = s.rounds.includes(k);
          return (
            <div key={k} className={`rounded-xl border p-3 text-center transition-colors ${on ? "border-indigo-200 bg-indigo-50" : "border-slate-200 bg-slate-50 opacity-60"}`}>
              <I className={`w-5 h-5 mx-auto ${on ? "text-indigo-600" : "text-slate-400"}`} />
              <div className={`mt-1.5 text-xs font-medium ${on ? "text-slate-900" : "text-slate-500"}`}>{l}</div>
              <div className={`mt-0.5 text-[10px] font-mono ${on ? "text-emerald-600" : "text-slate-400"}`}>{on ? "ON" : "OPTIONAL"}</div>
            </div>
          );
        })}
      </div>
      <div className="mt-6 text-[11px] font-mono uppercase tracking-widest text-slate-400">Typical roles</div>
      <div className="mt-3 flex flex-wrap gap-2">{s.roles.map((r) => <span key={r} className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-slate-700 bg-white">{r}</span>)}</div>
      <div className="mt-6 pt-5 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-500"><Sparkles className="w-4 h-4 text-indigo-500" /> Every candidate gets an AI scorecard and recommendation.</div>
    </div>
  );
}

export function Segments() {
  const [seg, setSeg] = useState("it");
  const s = SEGMENTS[seg];
  return (
    <section id="solutions" className="bg-slate-50 border-y border-slate-100 scroll-mt-16" data-testid="solutions-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <SectionHead eyebrow="Solutions" title="Built for how you hire." sub="From IT services and tech teams to contact centres, campus drives and enterprise HR — RecruitSift adapts to your hiring model." />
        <div className="mt-10 flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap" role="tablist">
          {Object.entries(SEGMENTS).map(([k, v]) => (
            <button key={k} role="tab" aria-selected={seg === k} onClick={() => setSeg(k)} data-testid={`segment-tab-${k}`}
              className={`shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium border transition-colors ${seg === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400 hover:text-slate-900"}`}>
              <v.Icon className="w-4 h-4" />{v.label}
            </button>
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={seg} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
            className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center [&>*]:min-w-0" data-testid={`segment-panel-${seg}`}>
            <div>
              <h3 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 leading-tight">{s.title}</h3>
              <ul className="mt-7 space-y-4">
                {s.points.map((p) => <li key={p} className="flex items-start gap-3 text-slate-700"><span className="mt-0.5 w-6 h-6 rounded-full bg-indigo-50 border border-indigo-100 grid place-items-center shrink-0"><Check className="w-3.5 h-3.5 text-indigo-600" /></span>{p}</li>)}
              </ul>
            </div>
            <SegmentCard s={s} />
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

const ENTERPRISE = [
  { Icon: Layers, t: "Multi-tenant workspaces", d: "Fully isolated data per organisation, with clients, accounts and service lines inside each." },
  { Icon: ShieldCheck, t: "Role-based permissions", d: "Tenant Admin, TAG Head, TAG Lead, TA Lead, Recruiter and Viewer — with a matrix admins can customise." },
  { Icon: Users2, t: "Team scoping", d: "Managers see their own teams' jobs and candidates; recruiters see only their own." },
  { Icon: Mail, t: "Branded communication", d: "Invites, reminders and reports carry your logo and sender name." },
  { Icon: Search, t: "Global candidate search", d: "Find any candidate by name, email or phone across every job — instantly." },
  { Icon: FileLock2, t: "Integrity & audit trail", d: "Session timelines, integrity checks and full transcripts on every report." },
];

export function EnterpriseGrid() {
  return (
    <section id="enterprise" className="bg-white scroll-mt-16" data-testid="enterprise-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <SectionHead eyebrow="Enterprise ready" title="Control, governance and scale — built in." />
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 rounded-3xl overflow-hidden border border-slate-200">
          {ENTERPRISE.map(({ Icon, t, d }, i) => (
            <Reveal key={t} delay={i * 0.05} className="bg-white">
              <div className="group h-full p-7 hover:bg-slate-50/60 transition-colors" data-testid={`enterprise-card-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-slate-900 group-hover:bg-indigo-600 grid place-items-center transition-colors"><Icon className="w-5 h-5 text-white" /></div>
                <div className="mt-5 font-display text-lg font-semibold text-slate-900">{t}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-sm text-slate-500">Coming soon: native ATS sync with Greenhouse, Lever and Workday.</p>
      </div>
    </section>
  );
}

const BEFORE = ["Recruiters spend 30–45 minutes on every first-round call", "Days of calendar ping-pong before anyone is assessed", "Notes vary by recruiter — clients can't compare candidates", "Skills claimed on the CV, rarely verified"];
const AFTER = ["Zero recruiter time on first-round screens", "Candidates interview 24/7, the same day they're invited", "Every candidate scored on the same questions and rubric", "Structured interviews and proctored coding verify skills"];

export function BeforeAfter() {
  return (
    <section className="relative bg-[#090D16] overflow-hidden" data-testid="before-after-section">
      <div className="absolute -bottom-40 right-0 w-[500px] h-[500px] rounded-full bg-cyan-500/10 blur-[120px]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <SectionHead dark center eyebrow="Why teams switch" title="Your first round, before and after RecruitSift." />
        <div className="mt-14 grid md:grid-cols-2 gap-5">
          <Reveal className="rounded-3xl border border-white/10 bg-white/[0.02] p-7 sm:p-9">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Before</div>
            <ul className="mt-6 space-y-4">{BEFORE.map((t) => <li key={t} className="flex gap-3 text-slate-400"><X className="w-5 h-5 text-rose-400/80 shrink-0" />{t}</li>)}</ul>
          </Reveal>
          <Reveal delay={0.1} className="rounded-3xl border border-indigo-400/30 bg-gradient-to-br from-indigo-500/15 to-cyan-500/5 p-7 sm:p-9">
            <div className="text-xs font-mono uppercase tracking-widest text-cyan-300">With RecruitSift</div>
            <ul className="mt-6 space-y-4">{AFTER.map((t) => <li key={t} className="flex gap-3 text-white"><Check className="w-5 h-5 text-emerald-400 shrink-0" />{t}</li>)}</ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

const QUOTES = [
  { q: "We now send clients scored shortlists within a day of opening a requisition. The scorecards do the convincing for us.", n: "Marcus Vance", r: "Head of Technical Staffing, Apex Staffing" },
  { q: "The proctored coding round filtered out candidates who couldn't back up their CVs — before any of our engineers spent time.", n: "Elena Rostova", r: "VP Engineering, CloudScale IT" },
  { q: "The telephonic round screens our voice-process applicants around the clock — recruiters only speak to the ones who are ready for customers.", n: "Riya Kapoor", r: "TA Manager, Meridian BPO Services" },
];

export function Testimonials() {
  return (
    <section className="bg-slate-50 border-y border-slate-100" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <SectionHead eyebrow="Customer stories" title="What hiring leaders say." sub="Illustrative examples of how teams use RecruitSift." />
        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {QUOTES.map((t, i) => (
            <Reveal key={t.n} delay={i * 0.08}>
              <figure className="h-full rounded-3xl bg-white border border-slate-200 p-7 flex flex-col hover:shadow-xl hover:-translate-y-1 transition-[box-shadow,transform] duration-300" data-testid={`testimonial-${i}`}>
                <div className="flex items-center justify-between"><Quote className="w-7 h-7 text-indigo-200" /><span className="text-[10px] uppercase tracking-widest font-mono px-2 py-1 rounded-full bg-slate-100 text-slate-500">Example</span></div>
                <blockquote className="mt-5 text-slate-800 leading-relaxed flex-1">"{t.q}"</blockquote>
                <figcaption className="mt-6 pt-5 border-t border-slate-100"><div className="font-semibold text-slate-900 text-sm">{t.n}</div><div className="text-xs text-slate-500">{t.r}</div></figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function DemoVideo() {
  return (
    <section id="demo-video" className="bg-slate-50 border-t border-slate-100 scroll-mt-16" data-testid="demo-video-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-24">
        <SectionHead center eyebrow={<><Play className="w-3 h-3" fill="currentColor" /> 60-second tour</>} title="See RecruitSift in action." />
        <Reveal className="mt-10 rounded-3xl overflow-hidden shadow-2xl ring-1 ring-slate-900/10 bg-slate-900">
          <div className="relative" style={{ paddingTop: "56.25%" }}>
            <iframe className="absolute inset-0 w-full h-full" src="https://www.youtube.com/embed/PVHiRvhnHi8?rel=0&modestbranding=1" title="RecruitSift — 60-second product demo"
              frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen loading="lazy" data-testid="youtube-demo" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function FinalCta() {
  const { openDemo } = useDemo();
  return (
    <section className="relative bg-[#090D16] overflow-hidden" data-testid="final-cta-section">
      <div className="absolute inset-0 rs-grid-dark [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]" />
      <div className="absolute left-1/2 -translate-x-1/2 -top-32 w-[700px] h-[400px] rounded-full bg-indigo-600/25 blur-[120px]" />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-center">
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight leading-tight">Put your first round on autopilot.</h2>
        <p className="mt-5 text-slate-400 text-base md:text-lg">See RecruitSift interview candidates for your own roles, or start a free trial and invite your first candidate today.</p>
        <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
          <button onClick={openDemo} data-testid="cta-btn-book-demo" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white hover:bg-indigo-50 text-slate-900 font-semibold transition-colors"><CalendarCheck2 className="w-4 h-4" /> Book a Demo</button>
          <Link to="/register" data-testid="cta-btn-start-trial" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-white/20 hover:border-white/50 text-white font-semibold transition-colors">Start Free Trial <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </div>
    </section>
  );
}
